import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { formatApiError } from '../services/api';
import { Car, Eye, EyeSlash } from '@phosphor-icons/react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1753030505007-98ceef668415?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBjYXIlMjBzdHVkaW8lMjBsaWdodGluZ3xlbnwwfHx8fDE3NzY0MzcxMjV8MA&ixlib=rb-4.1.0&q=85)'
        }}
      >
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <h1 className="text-4xl font-black tracking-tight mb-4">
            AutoVermietung
          </h1>
          <p className="text-lg text-white/80">
            Professionelles Flottenmanagement für Ihre Autovermietung
          </p>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#F7F8FA]">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-[#E60000] rounded-sm flex items-center justify-center">
              <Car weight="fill" className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold tracking-tight text-[#111418]">Anmelden</h2>
              <p className="text-sm text-[#525866]">Verwaltungssystem</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-sm text-red-700 text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <div>
              <label className="overline block mb-2">E-Mail</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-swiss w-full px-4 py-3"
                placeholder="admin@autovermietung.de"
                required
                data-testid="login-email-input"
              />
            </div>

            <div>
              <label className="overline block mb-2">Passwort</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-swiss w-full px-4 py-3 pr-12"
                  placeholder="••••••••"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8A94A6] hover:text-[#525866]"
                >
                  {showPassword ? <EyeSlash weight="bold" size={20} /> : <Eye weight="bold" size={20} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full py-3 text-base disabled:opacity-50"
              data-testid="login-submit-button"
            >
              {loading ? 'Wird angemeldet...' : 'Anmelden'}
            </button>
          </form>

          <p className="mt-8 text-center text-sm text-[#8A94A6]">
            © {new Date().getFullYear()} AutoVermietung Management System
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
